import { optionLabels } from '@/lib/utils/format.utils';
import {
  FIELD_LABELS,
  SELF_ID_ANSWERS,
  SELF_ID_ANSWER_LABEL,
  UNDERREPRESENTED_CATEGORY_OPTIONS,
  type SelfIdAnswer,
} from './constants';

/**
 * Narrows an untyped control/storage value to a self-identification answer.
 * Anything else (blank, a legacy boolean, an unknown token) is "unanswered".
 */
export function parseSelfIdAnswer(value: unknown): SelfIdAnswer | undefined {
  return SELF_ID_ANSWERS.find((answer) => answer === value);
}

/** Human label for a stored answer, "Not provided" when the founder skipped it. */
export function selfIdAnswerLabel(answer: SelfIdAnswer | null | undefined): string {
  return SELF_ID_ANSWER_LABEL[answer ?? 'unanswered'];
}

/** The self-identification answers as both the founder and admin reads carry them. */
export interface SelfIdentificationAnswers {
  founderFemale?: SelfIdAnswer | null;
  founderUnderrepresented?: SelfIdAnswer | null;
  founderUnderrepresentedCategories?: string[] | null;
}

/** One labelled read-only answer. */
export interface SelfIdentificationFact {
  label: string;
  value: string | null;
}

/**
 * The founding team's self-identification answers (UX sheet r113-r115) as
 * labelled facts, shared by the founder's profile view and the admin console.
 * An unanswered question reads "Not provided".
 *
 * Args: answers - the stored answers; emptyCategories - what to show when no
 *   category is ticked (each surface renders "empty" its own way).
 * Returns: the three facts in display order.
 */
export function buildSelfIdentificationFacts(
  answers: SelfIdentificationAnswers,
  emptyCategories: string | null = null,
): SelfIdentificationFact[] {
  return [
    { label: FIELD_LABELS.founderFemale, value: selfIdAnswerLabel(answers.founderFemale) },
    {
      label: FIELD_LABELS.founderUnderrepresented,
      value: selfIdAnswerLabel(answers.founderUnderrepresented),
    },
    {
      label: FIELD_LABELS.founderUnderrepresentedCategories,
      value:
        optionLabels(
          UNDERREPRESENTED_CATEGORY_OPTIONS,
          answers.founderUnderrepresentedCategories,
        ) ?? emptyCategories,
    },
  ];
}
